/**
 * Convert a self-contained JSX/JS file into a standalone HTML document.
 *
 * Strategy: fetch React, ReactDOM, and Tailwind CSS at upload time (in the
 * user's browser on lagbasn.se, no CSP restrictions), then inline everything
 * directly into the HTML. The resulting file is fully self-contained and
 * works under any CSP — no external CDN calls at runtime.
 */

const CDN = 'https://cdn.jsdelivr.net/npm';

async function fetchText(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status} loading ${url}`);
  return r.text();
}

async function ensureBabel() {
  if (typeof window !== 'undefined' && window.Babel) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${CDN}/@babel/standalone/babel.min.js`;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Kunde inte ladda Babel'));
    document.head.appendChild(s);
  });
}

// Prevent </script> inside inlined code from prematurely closing the tag
function safe(s) {
  return s.replace(/<\/script>/gi, '<\\/script>');
}

export async function jsxToHtml(source, filename) {
  let code = source
    // Multiline named imports
    .replace(/import\s*\{[\s\S]*?\}\s*from\s*['"][^'"]+['"];?/g, '')
    // Default + named
    .replace(/import\s+\w+\s*,\s*\{[\s\S]*?\}\s*from\s*['"][^'"]+['"];?/g, '')
    // Default only
    .replace(/import\s+\w+\s+from\s+['"][^'"]+['"];?/g, '')
    // Side-effect
    .replace(/import\s+['"][^'"]+['"];?/g, '')
    // CommonJS require
    .replace(/(?:const|let|var)\s+\{[\s\S]*?\}\s*=\s*require\(['"][^'"]+['"]\);?/g, '')
    .replace(/(?:const|let|var)\s+\w+\s*=\s*require\(['"][^'"]+['"]\);?/g, '')
    .trim();

  // Detect default export component name
  let componentName = 'App';
  const fnMatch  = code.match(/export\s+default\s+function\s+(\w+)/);
  const clsMatch = code.match(/export\s+default\s+class\s+(\w+)/);
  const varMatch = code.match(/export\s+default\s+(\w+)\s*;?\s*$/m);
  if (fnMatch)       componentName = fnMatch[1];
  else if (clsMatch) componentName = clsMatch[1];
  else if (varMatch) componentName = varMatch[1];

  // Strip export keywords
  code = code
    .replace(/export\s+default\s+function\s+/g, 'function ')
    .replace(/export\s+default\s+class\s+/g,    'class ')
    .replace(/export\s+default\s+\w+\s*;?\s*$/m, '')
    .replace(/export\s+(function|const|class|let|var)\s+/g, '$1 ');

  // Transpile JSX → plain JS using Babel (runs in user's browser)
  await ensureBabel();
  let transpiled;
  try {
    transpiled = window.Babel.transform(code, {
      presets: ['react'],
      filename,
    }).code;
  } catch (e) {
    throw new Error('Babel-transpileringsfel: ' + e.message);
  }

  // Append window assignment so const/let/function components are all reachable
  transpiled += `\ntry { window['${componentName}'] = ${componentName}; } catch (_e) {}`;

  // Fetch React + ReactDOM + Tailwind CSS in parallel — all inlined into HTML
  const [reactSrc, reactDomSrc, twCSS] = await Promise.all([
    fetchText(`${CDN}/react@18/umd/react.production.min.js`),
    fetchText(`${CDN}/react-dom@18/umd/react-dom.production.min.js`),
    Promise.all([
      fetchText(`${CDN}/tailwindcss@3.4.17/base.css`),
      fetchText(`${CDN}/tailwindcss@3.4.17/components.css`),
      fetchText(`${CDN}/tailwindcss@3.4.17/utilities.css`),
    ]).then(parts => parts.join('\n')).catch(() => '/* Tailwind unavailable */'),
  ]);

  const title = filename.replace(/\.(jsx?|tsx?)$/, '');

  return `<!DOCTYPE html>
<html lang="sv">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${title}</title>
<style>
${twCSS}
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; min-height: 100%; }
body { font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif; }
</style>
</head>
<body>
<div id="root"></div>

<script>${safe(reactSrc)}</script>
<script>${safe(reactDomSrc)}</script>
<script>
/* ── React globals ──────────────────────────────────── */
window.useState    = React.useState;
window.useEffect   = React.useEffect;
window.useMemo     = React.useMemo;
window.useCallback = React.useCallback;
window.useRef      = React.useRef;
window.useContext  = React.useContext;
window.createContext = React.createContext;
window.memo        = React.memo;
window.forwardRef  = React.forwardRef;
window.useReducer  = React.useReducer;
window.useLayoutEffect = React.useLayoutEffect;
window.useId       = React.useId;

function _esc(s) { return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
function _showError(msg) {
  document.getElementById('root').innerHTML =
    '<div style="padding:32px;font-family:monospace;background:#0a0a0b;min-height:100vh;color:#ef4444">'
    + '<div style="font-size:14px;font-weight:700;margin-bottom:12px">Fel vid rendering av JSX-fil</div>'
    + '<div style="font-size:13px;color:#fca5a5;white-space:pre-wrap">' + _esc(msg) + '</div>'
    + '</div>';
}

/* ── App ────────────────────────────────────────────── */
try {
${safe(transpiled)}
} catch (e) { _showError('Körningsfel:\\n' + e.message + (e.stack ? '\\n\\n' + e.stack : '')); }

/* ── Mount ──────────────────────────────────────────── */
try {
  var Root = window['${componentName}'];
  if (!Root) throw new Error('Komponent "${componentName}" hittades inte i globalt scope efter transpilering.');
  ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(Root));
} catch (e) { _showError('React render-fel:\\n' + e.message); }
</script>

</body>
</html>`;
}
